import { memo, useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Eye, FileWarning } from 'lucide-react'
import type { GridMediaIssue } from '../../lib/grid-media-issues.js'
import { formatOverlapAmount } from '../../lib/grid-overlaps.js'
import { formatDuration, formatTime } from '../../lib/utils.js'

interface MediaIssueBannerProps {
  issues: GridMediaIssue[]
  onShow: (issue: GridMediaIssue) => void
}

const KIND_HINT: Record<GridMediaIssue['kind'], string> = {
  OTHER_PROGRAM: 'rechoisissez le média de la case',
  SHORT_MEDIA:   'l’interlude couvrira la fin de case',
}

/**
 * Barre des cases dont le fichier ne colle pas, jumelle de la barre des
 * chevauchements et de même gravité : ces deux défauts finissent à l'antenne
 * sur autre chose que le programme annoncé. Calculée depuis la grille chargée
 * (`lib/grid-media-issues.ts`), elle disparaît d'elle-même une fois la case
 * corrigée.
 */
export const MediaIssueBanner = memo(function MediaIssueBanner({ issues, onShow }: MediaIssueBannerProps) {
  const [index, setIndex] = useState(0)
  const count = issues.length
  const current = issues[Math.min(index, count - 1)]

  useEffect(() => { if (index > 0 && index >= count) setIndex(Math.max(0, count - 1)) }, [count, index])

  const previous = useCallback(() => setIndex(i => (i - 1 + count) % count), [count])
  const next = useCallback(() => setIndex(i => (i + 1) % count), [count])
  const handleShow = useCallback(() => { if (current) onShow(current) }, [current, onShow])

  if (!current) return null

  return (
    <div className="agenda-gap-banner agenda-overlap-banner" role="alert">
      <FileWarning size={14} aria-hidden="true" className="agenda-gap-banner__icon" />

      <span className="agenda-gap-banner__text">
        <strong>{count === 1 ? '1 média à vérifier' : `${count} médias à vérifier`}</strong>
        {' — '}
        {formatTime(current.startTime).slice(0, 5)}
        {' « '}{current.title}{' » '}
        {current.kind === 'OTHER_PROGRAM'
          ? <>joue {current.fileName ? `« ${current.fileName} »` : 'un fichier'}, <strong>fichier d’une autre émission</strong></>
          : <>: le fichier dure {formatDuration(current.mediaDurationSec ?? 0)} pour une case de {formatDuration(current.slotDurationSec)}, <strong>il manque {formatOverlapAmount(current.shortfallSec)}</strong></>}
      </span>

      <span className="agenda-gap-banner__next agenda-overlap-banner__hint">{KIND_HINT[current.kind]}</span>

      <span className="agenda-gap-banner__actions">
        {count > 1 && (
          <span className="agenda-overlap-banner__nav" aria-label="Média affiché">
            <button type="button" className="tp-button" onClick={previous} aria-label="Média précédent">
              <ChevronLeft size={13} aria-hidden="true" />
            </button>
            <span className="agenda-overlap-banner__position">{Math.min(index, count - 1) + 1}/{count}</span>
            <button type="button" className="tp-button" onClick={next} aria-label="Média suivant">
              <ChevronRight size={13} aria-hidden="true" />
            </button>
          </span>
        )}
        <button type="button" className="tp-button" onClick={handleShow} title="Sélectionner la case dans la timeline pour corriger son média">
          <Eye size={13} aria-hidden="true" />
          Voir
        </button>
      </span>
    </div>
  )
})
